/**
 * Persistence of encrypted vaults.
 *
 * The default backend is IndexedDB (database `nuxaris-wallet`, object store `vaults`, keyed
 * by `accountId`). It is deliberately behind a tiny interface so that:
 *   - unit tests can run without an IndexedDB implementation, and
 *   - a future backend (extension storage, OPFS...) is a drop-in swap.
 *
 * Only ciphertext ever reaches this layer. `localStorage` / `sessionStorage` are never used
 * for anything: they are synchronous, string-only, and trivially readable by any script that
 * gets injected into the page.
 */

import { CorruptVaultError, StorageUnavailableError } from './errors';
import { KDF_NAME } from './vault-crypto';

/** Current on-disk format version. */
export const VAULT_VERSION = 1;
export const DB_NAME = 'nuxaris-wallet';
export const DB_VERSION = 1;
export const STORE_NAME = 'vaults';

/** The encrypted record as it sits in IndexedDB. Contains no plaintext key material. */
export interface StoredVault {
  version: number;
  accountId: string;
  /** Public key of the enclosed private key — lets the UI show an identity while locked. */
  publicKeyHex: string;
  fingerprint: string;
  /** base64, 16 random bytes. */
  salt: string;
  /** base64, 12 random bytes. */
  iv: string;
  /** base64, AES-256-GCM ciphertext with the 16-byte tag appended. */
  ciphertext: string;
  kdf: { name: string; iterations: number };
  createdAt: number;
  updatedAt: number;
}

/** Storage backend contract. Implementations must never throw for a missing record. */
export interface VaultStorage {
  get(accountId: string): Promise<StoredVault | null>;
  put(vault: StoredVault): Promise<void>;
  remove(accountId: string): Promise<void>;
  listIds(): Promise<string[]>;
}

const REQUIRED_STRING_FIELDS: Array<keyof StoredVault> = [
  'accountId',
  'publicKeyHex',
  'fingerprint',
  'salt',
  'iv',
  'ciphertext',
];

/** Shape-checks a record read back from storage. */
export function assertStoredVault(value: unknown): StoredVault {
  const record = value as StoredVault | null;
  if (!record || typeof record !== 'object') throw new CorruptVaultError();
  for (let i = 0; i < REQUIRED_STRING_FIELDS.length; i += 1) {
    const field = REQUIRED_STRING_FIELDS[i];
    if (typeof record[field] !== 'string' || (record[field] as string).length === 0) {
      throw new CorruptVaultError(`Stored vault is missing field "${field}"`);
    }
  }
  if (!record.kdf || typeof record.kdf.iterations !== 'number' || record.kdf.iterations <= 0) {
    throw new CorruptVaultError('Stored vault has no usable KDF parameters');
  }
  if (record.kdf.name !== KDF_NAME) {
    throw new CorruptVaultError(`Unsupported KDF "${record.kdf.name}"`);
  }
  if (record.version !== VAULT_VERSION) {
    throw new CorruptVaultError(
      `Stored vault version ${record.version} is not supported by this build (expected ${VAULT_VERSION})`,
    );
  }
  return record;
}

/* ------------------------------------------------------------------ IndexedDB backend */

interface GlobalWithIndexedDb {
  indexedDB?: IDBFactory;
}

function resolveFactory(factory?: IDBFactory): IDBFactory {
  if (factory) return factory;
  const scope = (typeof globalThis !== 'undefined' ? globalThis : window) as unknown as GlobalWithIndexedDb;
  if (!scope.indexedDB) throw new StorageUnavailableError();
  return scope.indexedDB;
}

/** True when the default IndexedDB backend can be used in this environment. */
export function isIndexedDbAvailable(): boolean {
  try {
    resolveFactory();
    return true;
  } catch (err) {
    return false;
  }
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new StorageUnavailableError('IndexedDB request failed'));
  });
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'accountId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new StorageUnavailableError('Cannot open IndexedDB'));
    request.onblocked = () => reject(new StorageUnavailableError('IndexedDB upgrade blocked by another tab'));
  });
}

async function withStore<T>(
  factory: IDBFactory,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDatabase(factory);
  try {
    const tx = db.transaction(STORE_NAME, mode);
    const result = await work(tx.objectStore(STORE_NAME));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error || new StorageUnavailableError('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error || new StorageUnavailableError('IndexedDB transaction failed'));
    });
    return result;
  } finally {
    db.close();
  }
}

/** The default backend. `factory` is injectable for tests (e.g. a fake IndexedDB). */
export function createIndexedDbStorage(factory?: IDBFactory): VaultStorage {
  return {
    async get(accountId: string): Promise<StoredVault | null> {
      const raw = await withStore(resolveFactory(factory), 'readonly', (store) =>
        promisifyRequest<unknown>(store.get(accountId) as IDBRequest<unknown>),
      );
      return raw === undefined || raw === null ? null : assertStoredVault(raw);
    },

    async put(vault: StoredVault): Promise<void> {
      await withStore(resolveFactory(factory), 'readwrite', (store) =>
        promisifyRequest<IDBValidKey>(store.put(vault) as IDBRequest<IDBValidKey>),
      );
    },

    async remove(accountId: string): Promise<void> {
      await withStore(resolveFactory(factory), 'readwrite', (store) =>
        promisifyRequest<undefined>(store.delete(accountId) as IDBRequest<undefined>),
      );
    },

    async listIds(): Promise<string[]> {
      const keys = await withStore(resolveFactory(factory), 'readonly', (store) =>
        promisifyRequest<IDBValidKey[]>(store.getAllKeys() as IDBRequest<IDBValidKey[]>),
      );
      const out: string[] = [];
      for (let i = 0; i < keys.length; i += 1) {
        if (typeof keys[i] === 'string') out.push(keys[i] as string);
      }
      return out;
    },
  };
}

/* --------------------------------------------------------------------- memory backend */

/**
 * Volatile backend. Intended for tests and for environments without IndexedDB (SSR,
 * private-mode Safari quirks). Nothing survives a reload, so a vault created here is lost
 * unless the user still has their recovery phrase.
 */
export function createMemoryStorage(seed?: StoredVault[]): VaultStorage {
  const records: { [accountId: string]: StoredVault } = Object.create(null) as {
    [accountId: string]: StoredVault;
  };
  if (seed) {
    for (let i = 0; i < seed.length; i += 1) records[seed[i].accountId] = seed[i];
  }

  return {
    get(accountId: string): Promise<StoredVault | null> {
      const found = records[accountId];
      return Promise.resolve(found ? assertStoredVault(found) : null);
    },
    put(vault: StoredVault): Promise<void> {
      records[vault.accountId] = vault;
      return Promise.resolve();
    },
    remove(accountId: string): Promise<void> {
      delete records[accountId];
      return Promise.resolve();
    },
    listIds(): Promise<string[]> {
      return Promise.resolve(Object.keys(records));
    },
  };
}

/* ------------------------------------------------------------------------- resolution */

let injected: VaultStorage | null = null;
let defaultStorage: VaultStorage | null = null;

/**
 * Overrides the storage backend process-wide (tests, or a host app that wants its own
 * persistence). Pass `null` to fall back to IndexedDB.
 */
export function setVaultStorage(storage: VaultStorage | null): void {
  injected = storage;
  defaultStorage = null;
}

/** The backend currently in use. Throws {@link StorageUnavailableError} if there is none. */
export function getVaultStorage(): VaultStorage {
  if (injected) return injected;
  if (!defaultStorage) {
    resolveFactory(); // throws StorageUnavailableError when IndexedDB is missing
    defaultStorage = createIndexedDbStorage();
  }
  return defaultStorage;
}
